import { QueryResponse } from "~/modules/filters/types";
import { ServiceProvider } from "~/modules/service_providers";
import { Card, CardContent } from "~/components/ui/card";
import { Anchor } from "~/components/Anchor";
import { ImageLoader } from "~/components/ImageLoader";
import { RWA_COIN_ICON } from "~/consts";
import { SectionHeader } from "~/components/SectionHeader";
import { NetworkUserEvents } from "~/modules/analytics/events/networks";
import { Badge, BadgeVariants } from "~/components/Badge";
import { ArrowUpRightIcon } from "lucide-react";
import { cn } from "~/lib/utils";

// // // //

function ServiceProviderCard(props: { serviceProvider: ServiceProvider }) {
    const { serviceProvider } = props;

    // Return null if there is no website for the service provider
    if (!serviceProvider.website) {
        return null;
    }

    return (
        <Anchor
            href={serviceProvider.website || ""}
            openInNewTab
            analyticsLabel={
                NetworkUserEvents.ClickNetworkServiceProviderWebsite
            }
            className="block"
        >
            <Card className="overflow-hidden shadow-xs hover:shadow-lg rounded-lg border transition-all group relative h-full">
                <CardContent className="p-6">
                    <ArrowUpRightIcon className="absolute top-4 right-4 w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity text-secondary-foreground" />
                    <div
                        className={cn("flex items-start gap-4", {
                            "mb-3": serviceProvider.description,
                        })}
                    >
                        <ImageLoader
                            src={serviceProvider.icon_url || RWA_COIN_ICON}
                            alt={`${serviceProvider.name} logo`}
                            fallbackSrc={RWA_COIN_ICON}
                            className="rounded-full bg-white h-12 w-12 shrink-0"
                            fallbackClassName="bg-white opacity-50"
                        />
                        <div>
                            <h2 className="text-lg font-medium text-foreground mb-1">
                                {serviceProvider.name}
                            </h2>
                            {serviceProvider.types &&
                                serviceProvider.types.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {serviceProvider.types.map((type) => (
                                            <Badge
                                                key={type}
                                                size="sm"
                                                label={type}
                                                variant={BadgeVariants.gray}
                                                // className="bg-gray-900/15!"
                                            />
                                        ))}
                                    </div>
                                )}
                        </div>
                    </div>
                    {serviceProvider.description && (
                        <p className="text-sm text-secondary-foreground">
                            {serviceProvider.description}
                        </p>
                    )}
                </CardContent>
            </Card>
        </Anchor>
    );
}

// // // //

export function NetworkServiceProviders(props: {
    serviceProviders: QueryResponse<ServiceProvider>;
}) {
    const { serviceProviders } = props;
    const { results } = serviceProviders;

    // Render null if there are no service providers
    if (results.length === 0) {
        return null;
    }

    // Return null if there are no service providers with a website field
    if (results.every((sp) => !sp.website)) {
        return null;
    }

    return (
        <div className="py-6">
            <SectionHeader label="Ecosystem" />

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-4 gap-4 mt-4">
                {serviceProviders.results.map((sp) => (
                    <ServiceProviderCard key={sp.id} serviceProvider={sp} />
                ))}
            </div>
        </div>
    );
}
